import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";

const COLORS = {
  primary: [15, 23, 42],
  accent: [255, 186, 69],
  red: [239, 68, 68],
  green: [34, 197, 94],
  yellow: [234, 179, 8],
  gray: [107, 114, 128],
  lightGray: [243, 244, 246],
  white: [255, 255, 255],
  text: [31, 41, 55],
};

// Pre-load logo for PDF header
let logoDataUrl = null;
const loadLogo = () => {
  return new Promise((resolve) => {
    if (logoDataUrl) return resolve(logoDataUrl);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      logoDataUrl = canvas.toDataURL("image/png");
      resolve(logoDataUrl);
    };
    img.onerror = () => resolve(null);
    img.src = "/assets/images/logo2-white-version-2.png";
  });
};

const addHeader = (doc, title, period, logoData) => {
  // Dark header background
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, 210, 36, "F");
  // Logo or text fallback
  let logoRendered = false;
  if (logoData) {
    try {
      doc.addImage(logoData, "PNG", 12, 4, 32, 12);
      logoRendered = true;
    } catch (e) { /* fallback to text */ }
  }
  if (!logoRendered) {
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.accent);
    doc.text("Ogiso", 14, 14);
  }
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...COLORS.white);
  doc.text(title, 14, 24);
  if (period?.from && period?.to) {
    const from = moment(period.from).format("MMM D, YYYY");
    const to = moment(period.to).format("MMM D, YYYY");
    doc.setFontSize(8);
    doc.text(`Period: ${from} – ${to}`, 14, 31);
  }
  doc.setFontSize(8);
  doc.setTextColor(180, 180, 180);
  doc.text(`Generated ${moment().format("MMM D, YYYY h:mm A")}`, 196, 15, { align: "right" });
};

const addSectionTitle = (doc, title, y) => {
  if (y > 260) { doc.addPage(); y = 20; }
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.text);
  doc.text(title, 14, y);
  doc.setDrawColor(...COLORS.lightGray);
  doc.setLineWidth(0.4);
  doc.line(14, y + 2, 196, y + 2);
  return y + 10;
};

const addKpiRow = (doc, kpis, y) => {
  if (y > 250) { doc.addPage(); y = 20; }
  const colW = (196 - 14) / kpis.length;
  kpis.forEach((kpi, i) => {
    const x = 14 + i * colW;
    doc.setFillColor(...COLORS.lightGray);
    doc.roundedRect(x, y, colW - 4, 20, 3, 3, "F");
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...(kpi.color || COLORS.text));
    doc.text(String(kpi.value ?? "—"), x + (colW - 4) / 2, y + 10, { align: "center" });
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.gray);
    doc.text(kpi.label, x + (colW - 4) / 2, y + 17, { align: "center" });
  });
  return y + 28;
};

// Render a client-level detail table with missing dates
const addClientDetailTable = (doc, clients, y, categoryLabel) => {
  if (!clients || clients.length === 0) return y;

  const tableBody = [];
  clients.forEach((c) => {
    const name = c.clientName || "Unknown";
    const count = c.missingCount || c.totalMissed || 0;
    const dates = (c.missingDates || c.dates || [])
      .map((d) => {
        if (typeof d === "string") return moment(d).format("MMM D");
        if (d?.date) return moment(d.date).format("MMM D") + (d.count ? ` (${d.count})` : "");
        return moment(d).format("MMM D");
      })
      .join(", ");
    tableBody.push([name, String(count), dates]);
  });

  doc.autoTable({
    startY: y,
    head: [[categoryLabel || "Client", "Missing Days", "Missing Dates"]],
    body: tableBody,
    theme: "striped",
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: "bold", fontSize: 8 },
    bodyStyles: { fontSize: 7, textColor: COLORS.text },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    margin: { left: 14, right: 14 },
    styles: { cellPadding: 2, overflow: "linebreak" },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 20, halign: "center" },
      2: { cellWidth: "auto" },
    },
  });
  return doc.lastAutoTable.finalY + 8;
};

// Render rule breakdown tables (for notes/tasks with multiple rules)
const addRuleBreakdown = (doc, rules, y) => {
  if (!rules || rules.length === 0) return y;
  rules.forEach((rule) => {
    if (y > 250) { doc.addPage(); y = 20; }
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.text);
    doc.text(`Rule: ${rule.ruleName || "Default"}`, 14, y);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.gray);
    doc.text(`${rule.total || rule.clients?.length || 0} client(s) affected`, 14, y + 5);
    y += 10;
    if (rule.clients?.length > 0) {
      y = addClientDetailTable(doc, rule.clients, y, "Client");
    }
  });
  return y;
};

export const generateAuditPDF = async (auditData, auditType, title) => {
  if (!auditData) return;
  const logoData = await loadLogo();
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  addHeader(doc, title, auditData.period, logoData);
  let y = 46;

  if (auditType === "full") {
    // ── Overview KPIs ──
    y = addSectionTitle(doc, "Audit Summary", y);
    y = addKpiRow(doc, [
      { label: "Total Issues", value: auditData.totalIssues ?? 0, color: auditData.totalIssues > 0 ? COLORS.red : COLORS.green },
      { label: "Document Issues", value: ((auditData.documents?.expired || 0) + (auditData.documents?.missing || 0)) || 0 },
      { label: "Notes Issues", value: auditData.notes?.total ?? 0 },
      { label: "Task Issues", value: auditData.tasks?.total ?? 0 },
      { label: "MAR Issues", value: auditData.mar?.total ?? 0 },
    ], y);

    // ── Document Compliance ──
    y = addSectionTitle(doc, "Document Compliance", y);
    const docs = auditData.documents || {};
    y = addKpiRow(doc, [
      { label: "Expired", value: docs.expired ?? 0, color: (docs.expired ?? 0) > 0 ? COLORS.red : COLORS.green },
      { label: "Missing", value: docs.missing ?? 0 },
      { label: "Expiring Soon", value: docs.expiring ?? 0, color: (docs.expiring ?? 0) > 0 ? COLORS.yellow : COLORS.green },
    ], y);
    if (docs.expiredItems?.length > 0) {
      doc.autoTable({
        startY: y,
        head: [["Document", "Entity", "Status"]],
        body: docs.expiredItems.map((d) => [d?.name || d?.fileName || "Document", d?.entityName || d?.staffName || "—", "Expired"]),
        theme: "striped",
        headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: "bold", fontSize: 8 },
        bodyStyles: { fontSize: 8, textColor: COLORS.text },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { left: 14, right: 14 },
        styles: { cellPadding: 2 },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    // ── Notes Audit Detail ──
    if (auditData.notes?.clients?.length > 0) {
      y = addSectionTitle(doc, "Notes Compliance — Missing Entries", y);
      y = addKpiRow(doc, [
        { label: "Clients Affected", value: auditData.notes.total ?? 0, color: COLORS.red },
      ], y);
      if (auditData.notes.rules?.length > 0) {
        y = addRuleBreakdown(doc, auditData.notes.rules, y);
      } else {
        y = addClientDetailTable(doc, auditData.notes.clients, y, "Client");
      }
    }

    // ── Tasks Audit Detail ──
    if (auditData.tasks?.clients?.length > 0) {
      y = addSectionTitle(doc, "Task Compliance — Missing Entries", y);
      y = addKpiRow(doc, [
        { label: "Clients Affected", value: auditData.tasks.total ?? 0, color: COLORS.red },
      ], y);
      if (auditData.tasks.rules?.length > 0) {
        y = addRuleBreakdown(doc, auditData.tasks.rules, y);
      } else {
        y = addClientDetailTable(doc, auditData.tasks.clients, y, "Client");
      }
    }

    // ── MAR Audit Detail ──
    if (auditData.mar?.clients?.length > 0) {
      y = addSectionTitle(doc, "MAR Compliance — Missed Entries", y);
      y = addKpiRow(doc, [
        { label: "Clients Affected", value: auditData.mar.total ?? 0, color: COLORS.red },
      ], y);
      y = addClientDetailTable(doc, auditData.mar.clients, y, "Client");
    }

  } else {
    // ── Single-type audit ──
    const data = auditData[auditType] || auditData;
    y = addSectionTitle(doc, "Summary", y);
    y = addKpiRow(doc, [
      { label: "Clients Affected", value: data.total ?? 0, color: (data.total ?? 0) > 0 ? COLORS.red : COLORS.green },
    ], y);

    if (data.rules?.length > 0) {
      y = addRuleBreakdown(doc, data.rules, y);
    } else if (data.clients?.length > 0) {
      y = addSectionTitle(doc, "Client Detail — Missing Dates", y);
      y = addClientDetailTable(doc, data.clients, y, "Client");
    }
  }

  // ── Footer ──
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.gray);
    doc.text(`Page ${i} of ${pageCount}`, 196, 290, { align: "right" });
    doc.text("Ogiso — Confidential", 14, 290);
  }

  const filename = `${title.replace(/\s+/g, "_")}_${moment().format("YYYY-MM-DD")}.pdf`;
  doc.save(filename);
};
